import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

const toDate = (value: Date | string): Date =>
  value instanceof Date ? value : new Date(value);

/**
 * Standard timezone abbreviation mapping
 * Maps IANA timezone identifiers to their standard abbreviations
 * This ensures consistent labels (e.g., IST instead of GMT+5:30)
 */
const TIMEZONE_ABBREVIATION_MAP: Record<string, string> = {
  'Asia/Kolkata': 'IST',
  'America/Toronto': 'EST', // Will be EDT during daylight saving
  'America/Los_Angeles': 'PST', // Will be PDT during daylight saving
  'America/Chicago': 'CST', // Will be CDT during daylight saving
  'America/Denver': 'MST', // Will be MDT during daylight saving
  'America/New_York': 'EST', // Will be EDT during daylight saving
  'America/Vancouver': 'PST', // Will be PDT during daylight saving
};

/**
 * Get timezone abbreviation for a given date and timezone
 * @param date - The date to format (in UTC)
 * @param timeZone - IANA timezone (e.g., 'America/Toronto', 'America/Los_Angeles', 'Asia/Kolkata')
 * @returns Timezone abbreviation (e.g., 'EST', 'EDT', 'PST', 'PDT', 'IST')
 */
export const getTimeZoneAbbreviation = (date: Date, timeZone?: string): string => {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // For IST (Asia/Kolkata), always return 'IST' since it doesn't have DST
  // and Intl API returns "GMT+5:30" instead of "IST"
  if (tz === 'Asia/Kolkata') {
    return 'IST';
  }
  
  // For other timezones with standard mappings, use Intl API to get DST-aware abbreviation
  // This ensures we get EST/EDT, PST/PDT correctly based on the date
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'short',
  });
  
  const parts = formatter.formatToParts(date);
  const tzName = parts.find((part) => part.type === 'timeZoneName')?.value;
  
  // If Intl returns a GMT offset (e.g., "GMT+5:30", "GMT+530"), use our mapping as fallback
  if (tzName && tzName.startsWith('GMT')) {
    return TIMEZONE_ABBREVIATION_MAP[tz] || tzName;
  }
  
  // Return the abbreviation from Intl API (works well for EST/EDT, PST/PDT, etc.)
  return tzName || TIMEZONE_ABBREVIATION_MAP[tz] || '';
};

/**
 * Format time in appointment taker's timezone
 * @param value - Date or date string (in UTC)
 * @param pattern - Date format pattern (default: 'h:mm a')
 * @param timeZone - Appointment taker's timezone (defaults to browser timezone)
 * @returns Formatted time string with timezone abbreviation
 */
export const formatTimeWithZone = (
  value: Date | string,
  pattern = 'h:mm a',
  timeZone?: string
): string => {
  const date = toDate(value);
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Format the time in the appointment taker's timezone
  const formattedTime = formatInTimeZone(date, tz, pattern);
  const abbreviation = getTimeZoneAbbreviation(date, tz);
  const result = abbreviation ? `${formattedTime} ${abbreviation}` : formattedTime;
  
  return result;
};

/**
 * Format date and time in appointment taker's timezone
 * @param value - Date or date string (in UTC)
 * @param datePattern - Date format pattern (default: 'EEEE, MMMM d, yyyy')
 * @param timePattern - Time format pattern (default: 'h:mm a')
 * @param timeZone - Appointment taker's timezone (defaults to browser timezone)
 * @returns Formatted date and time string with timezone abbreviation
 */
export const formatDateTimeWithZone = (
  value: Date | string,
  datePattern = 'EEEE, MMMM d, yyyy',
  timePattern = 'h:mm a',
  timeZone?: string
): string => {
  const date = toDate(value);
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = formatInTimeZone(date, tz, datePattern);
  const formattedTime = formatTimeWithZone(date, timePattern, tz);

  return `${formattedDate} • ${formattedTime}`;
};

/**
 * Convert UTC date to appointment taker's timezone
 * @param utcDate - Date in UTC
 * @param timeZone - Appointment taker's timezone (defaults to browser timezone)
 * @returns Date object in appointment taker's timezone
 */
export const convertToTimeZone = (
  utcDate: Date | string,
  timeZone?: string
): Date => {
  const date = toDate(utcDate);
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return toZonedTime(date, tz);
};

/**
 * Format date only (no time) in appointment taker's timezone
 * @param value - Date or date string (in UTC)
 * @param pattern - Date format pattern (default: 'MMMM d, yyyy')
 * @param timeZone - Appointment taker's timezone (defaults to browser timezone)
 * @returns Formatted date string
 */
export const formatDateWithZone = (
  value: Date | string,
  pattern = 'MMMM d, yyyy',
  timeZone?: string
): string => {
  const date = toDate(value);
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return formatInTimeZone(date, tz, pattern);
};

/**
 * Get time in minutes from a UTC date in a specific timezone
 * @param utcDate - Date in UTC
 * @param timeZone - IANA timezone (e.g., 'America/Toronto', 'America/Los_Angeles')
 * @returns Time in minutes (0-1439)
 */
export const getTimeInMinutesInZone = (utcDate: Date, timeZone: string): number => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(utcDate);
  const hour = parseInt(parts.find(p => p.type === 'hour')!.value);
  const minute = parseInt(parts.find(p => p.type === 'minute')!.value);
  return hour * 60 + minute;
};

/**
 * Push a calendar date to midday local time so toISOString() does not roll back the UTC day.
 */
export function normalizeDateForApi(date: Date): Date {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  return d;
}

/** Y-M-D from the date picker's calendar fields (not UTC). */
export function getCalendarDateParts(date: Date): { year: number; month: number; day: number } {
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}

/** YYYY-MM-DD for the calendar date shown in the picker. */
export function formatCalendarDate(date: Date): string {
  const { year, month, day } = getCalendarDateParts(date);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Noon on the picker's calendar day in the taker timezone. */
export function calendarDateToInstant(date: Date, timeZone: string): Date {
  const { year, month, day } = getCalendarDateParts(date);
  const pad = (n: number) => String(n).padStart(2, '0');
  return fromZonedTime(`${year}-${pad(month)}-${pad(day)}T12:00:00`, timeZone);
}

/** Day of week for the calendar date in the taker timezone: 0 = Sunday, 6 = Saturday. */
export function getDayOfWeekForCalendarDate(date: Date, timeZone: string): number {
  return getDayOfWeekInZone(calendarDateToInstant(date, timeZone), timeZone);
}

/** True when the picker's calendar date is strictly before today in the taker timezone. */
export function isCalendarDateBeforeToday(date: Date, timeZone: string): boolean {
  const dateStr = formatCalendarDate(date);
  const todayStr = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return dateStr < todayStr;
}

/** True when the picker's calendar date is today in the taker timezone. */
export function isCalendarDateToday(date: Date, timeZone: string): boolean {
  return formatCalendarDate(date) === new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Format the picker's calendar date label in the taker timezone. */
export function formatCalendarDateInZone(
  date: Date,
  pattern: string,
  timeZone: string
): string {
  return formatInTimeZone(calendarDateToInstant(date, timeZone), timeZone, pattern);
}

const WEEKDAY_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** Day of week in a timezone: 0 = Sunday, 6 = Saturday */
export function getDayOfWeekInZone(date: Date, timeZone: string): number {
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
  }).format(date);
  return WEEKDAY_TO_INDEX[weekday] ?? date.getDay();
}

/**
 * Earliest bookable slot start for today in the taker timezone.
 * At 3:05 PM with 30-min slots, returns 3:30 PM (current slot 3:00–3:30 is excluded).
 */
export function getMinSlotStartForToday(timeZone: string, slotMinutes = 30): Date {
  const now = new Date();
  const nowMins = getTimeInMinutesInZone(now, timeZone);
  const currentSlotStart = Math.floor(nowMins / slotMinutes) * slotMinutes;
  const nextSlotStart = currentSlotStart + slotMinutes;
  const h = Math.floor(nextSlotStart / 60);
  const m = nextSlotStart % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const todayStr = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return fromZonedTime(`${todayStr}T${pad(h)}:${pad(m)}:00`, timeZone);
}

/** True when the calendar date is strictly before today in the given timezone */
export function isBeforeTodayInZone(date: Date, timeZone: string): boolean {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const todayStr = fmt.format(new Date());
  const dateStr = fmt.format(date);
  return dateStr < todayStr;
}

